import { Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

interface BlogSection {
  heading?: string;
  paragraphs?: string[];
  image?: string;
  quote?: string;
}

interface BlogPostDetail {
  id: number;
  title: string;
  slug: string;
  category: string;
  categoryLabel: string;
  excerpt: string;
  date: string;
  readTime: string;
  image: string;
  author: string;
  tags: string[];
  sections: BlogSection[];
}

@Component({
  selector: 'app-news-stories-detail',
  templateUrl: './news-stories-detail.component.html',
  styleUrls: ['./news-stories-detail.component.css']
})
export class NewsStoriesDetailComponent implements OnInit {
  currentSlug: string = '';
  post: BlogPostDetail | null = null;

  allPosts: BlogPostDetail[] = [
    /* 1. THE ART OF A PERFECT CUP */
    {
      id: 1,
      title: 'The Art of a Perfect Cup',
      slug: 'the-art-of-a-perfect-cup',
      category: 'Coffee Culture',
      categoryLabel: 'COFFEE CULTURE',
      excerpt:
        'Great coffee is more than a drink — it is a moment, a craft, and a passion. Discover how we bring the best beans, roasted to perfection, straight to your cup.',
      date: 'May 20, 2025',
      readTime: '5 min read',
      image: '/assets/images/art-of-cup.jpg',
      author: 'Liberica Team',
      tags: [
        'Coffee Culture',
        'Perfect Cup',
        'Coffee Beans',
        'Roasting',
        'Brewing',
        'Artisan Coffee'
      ],
      sections: [
        {
          heading: 'It Starts with the Bean',
          paragraphs: [
            'The journey to a perfect cup begins at the source. We work with passionate coffee farmers who cultivate high-quality beans with care for the environment and their communities.',
            'Every bean carries the character of its origin, from the soil and climate to the people who nurture it. That is why selecting the right beans is one of the most important parts of creating exceptional coffee.'
          ],
          image: '/assets/images/starts-with-bean.jpg'
        },
        {
          heading: 'The Art of Roasting',
          paragraphs: [
            'Roasting is where the magic happens. Our expert roasters carefully monitor time and temperature to unlock the unique flavours hidden inside every bean.',
            'A carefully controlled roast creates the balance between aroma, body, acidity and sweetness that makes every cup memorable.'
          ],
          image: '/assets/images/art-of-roasting.jpg'
        },
        {
          heading: 'Brewing with Passion',
          paragraphs: [
            'From traditional methods to modern brewing techniques, we explore every way to bring out the best in our beans.',
            'Whether it is a handcrafted espresso, a smooth pour-over, or a refreshing cold brew, our baristas approach every cup with precision and passion.'
          ],
          image: '/assets/images/brewing-with-passion.jpg',
          quote:
            'Coffee is not just what we do. It is who we are. Every cup is a connection — to the land, to the people, and to moments that matter.'
        }
      ]
    },

    /* 2. COLD BREW VS ICED COFFEE */
    {
      id: 2,
      title: 'Cold Brew vs Iced Coffee: What’s the Difference?',
      slug: 'cold-brew-vs-iced-coffee',
      category: 'Brewing Guide',
      categoryLabel: 'BREWING GUIDE',
      excerpt:
        'Learn the key differences between cold brew and iced coffee and discover which chilled coffee experience suits your taste best.',
      date: 'May 18, 2025',
      readTime: '4 min read',
      image: '/assets/images/cold-brew-vs-iced-coffee.jpg',
      author: 'Liberica Team',
      tags: ['Cold Brew', 'Iced Coffee', 'Brewing Guide', 'Coffee Tips'],
      sections: [
        {
          heading: 'What Is Cold Brew?',
          paragraphs: [
            'Cold brew is created by steeping coffee grounds in cold or room-temperature water for an extended period.',
            'This slower extraction creates a naturally smooth and mellow cup with lower perceived acidity and a rich mouthfeel.'
          ],
          image: '/assets/images/cold-brew.jpg'
        },
        {
          heading: 'What Is Iced Coffee?',
          paragraphs: [
            'Iced coffee is generally brewed using hot water and then chilled or poured directly over ice.',
            'Because hot water extracts coffee differently, iced coffee tends to have a brighter and more pronounced flavour.'
          ],
          image: '/assets/images/iced-coffee.jpg'
        },
        {
          heading: 'Which One Should You Choose?',
          paragraphs: [
            'If you prefer something smooth, rich and naturally mellow, cold brew may be your perfect match.',
            'If you enjoy a brighter coffee flavour with refreshing acidity, iced coffee is an excellent choice.'
          ],
          image: '/assets/images/choose.jpg',
          quote:
            'There is no right or wrong choice — the best coffee is the one that matches your moment.'
        }
      ]
    },

    /* 3. COZY CORNER */
    {
      id: 3,
      title: 'A Cozy Corner for Every Coffee Lover',
      slug: 'a-cozy-corner-for-every-coffee-lover',
      category: 'Cafe Updates',
      categoryLabel: 'CAFE UPDATES',
      excerpt:
        'Step inside Liberica MY Hub and experience a space designed for conversations, comfort, creativity, and great coffee.',
      date: 'May 15, 2025',
      readTime: '3 min read',
      image: '/assets/images/cozy-corner.jpg',
      author: 'Liberica Team',
      tags: ['Cafe Updates', 'Liberica MY Hub', 'Cafe Life', 'Community'],
      sections: [
        {
          heading: 'A Space Designed for Connection',
          paragraphs: [
            'A great café is more than tables and chairs. It is a place where people come together, conversations begin and ideas take shape.',
            'At Liberica MY Hub, we wanted to create an environment where everyone can find their own comfortable corner.'
          ],
          image: '/assets/images/liberica-visit-us-hero.jpg'
        },
        {
          heading: 'Comfort Meets Coffee',
          paragraphs: [
            'Warm textures, inviting spaces and the aroma of freshly brewed coffee come together to create a relaxed atmosphere.',
            'Whether you are catching up with friends, working on a project or simply enjoying a quiet cup, there is always a place for you.'
          ],
          image: '/assets/images/comfort.jpg'
        },
        {
          heading: 'Your Corner at Liberica',
          paragraphs: [
            'Our café is designed to become part of your everyday moments — from morning coffee rituals to afternoon conversations.'
          ],
          image: '/assets/images/corner.jpg',
          quote: 'Come for the coffee. Stay for the conversations.'
        }
      ]
    },

    /* 4. FARM TO CUP */
    {
      id: 4,
      title: 'From Farm to Cup: Our Coffee Journey',
      slug: 'from-farm-to-cup',
      category: 'Coffee Culture',
      categoryLabel: 'COFFEE CULTURE',
      excerpt:
        'We take pride in ethical sourcing and the journey of our coffee beans from passionate farmers all the way to your cup.',
      date: 'May 10, 2025',
      readTime: '6 min read',
      image: '/assets/images/farm-to-cup.jpg',
      author: 'Liberica Team',
      tags: ['Coffee Culture', 'Farm to Cup', 'Coffee Farmers', 'Ethical Sourcing'],
      sections: [
        {
          heading: 'Where Every Journey Begins',
          paragraphs: [
            'Every exceptional coffee begins with farmers who dedicate their time and knowledge to cultivating healthy coffee plants.',
            'The environment, altitude, rainfall and farming practices all contribute to the final character of the coffee.'
          ],
          image: '/assets/images/journery.jpg'
        },
        {
          heading: 'From Harvest to Roastery',
          paragraphs: [
            'After harvesting, coffee cherries go through carefully controlled processing before the beans begin their journey to the roastery.',
            'Quality at every stage matters because each step influences the flavours eventually found in your cup.'
          ],
          image: '/assets/images/harvest.jpg'
        },
        {
          heading: 'The Final Cup',
          paragraphs: [
            'Our job is to respect the work that has already gone into every bean and bring its best qualities forward through careful roasting and brewing.'
          ],
          image: '/assets/images/good-coffee.jpg',
          quote: 'Behind every cup is a journey worth discovering.'
        }
      ]
    },

    /* 6. DESSERTS */
    {
      id: 6,
      title: 'New Desserts to Pair With Your Coffee',
      slug: 'new-desserts-to-pair-with-your-coffee',
      category: 'Cafe Updates',
      categoryLabel: 'CAFE UPDATES',
      excerpt:
        'Discover our latest dessert selections and find the perfect sweet companion for your favourite Liberica coffee.',
      date: 'May 5, 2025',
      readTime: '2 min read',
      image: '/assets/images/snacks-deserts.jpg',
      author: 'Liberica Team',
      tags: ['Desserts', 'Cafe Updates', 'Coffee Pairing'],
      sections: [
        {
          heading: 'Why Coffee and Dessert Work So Well',
          paragraphs: [
            'Coffee naturally pairs with a wide variety of sweet flavours. Its bitterness and aroma can balance sweetness while bringing out subtle flavours in desserts.',
            'The right combination can turn a simple coffee break into a memorable experience.'
          ],
          image: '/assets/images/coffee-deserts.jpg'
        },
        {
          heading: 'Find Your Favourite Pairing',
          paragraphs: [
            'From creamy cakes to rich chocolate desserts, there is always something delicious waiting to accompany your favourite Liberica beverage.'
          ],
          image: '/assets/images/pairing.jpeg',
          quote:
            'Sometimes the perfect coffee moment simply needs something sweet on the side.'
        }
      ]
    },

    /* 7. COFFEE TASTING WORKSHOP */
    // {
    //   id: 7,
    //   title: 'Upcoming Coffee Tasting Workshop',
    //   slug: 'upcoming-coffee-tasting-workshop',
    //   category: 'Events',
    //   categoryLabel: 'EVENTS',
    //   excerpt:
    //     'Join us for an immersive coffee tasting experience where you can explore aromas, flavours, origins and brewing techniques.',
    //   date: 'May 2, 2025',
    //   readTime: '2 min read',
    //   image: '/assets/images/liberica-visit-us-hero.jpg',
    //   author: 'Liberica Team',
    //   tags: ['Events', 'Coffee Tasting', 'Workshop', 'Coffee Education'],
    //   sections: [
    //     {
    //       heading: 'Discover Coffee Through Your Senses',
    //       paragraphs: [
    //         'Coffee tasting is an opportunity to slow down and appreciate the details hidden inside every cup.',
    //         'During our workshop, participants can explore aroma, acidity, body, sweetness and flavour.'
    //       ],
    //       image: '/assets/images/liberica-visit-us-hero.jpg'
    //     },
    //     {
    //       heading: 'What You Can Expect',
    //       paragraphs: [
    //         'Guests will discover different coffee origins, learn basic tasting techniques and explore how brewing methods influence flavour.',
    //         'Whether you are completely new to specialty coffee or already a coffee enthusiast, there is something to discover.'
    //       ],
    //       image: '/assets/images/liberica-visit-us-hero.jpg',
    //       quote:
    //         'Taste slowly. Notice more. Discover the story inside every bean.'
    //     }
    //   ]
    // },

    /* 8. WELCOME */
    {
      id: 8,
      title: 'Welcome to Liberica MY Hub',
      slug: 'welcome-to-liberica-my-hub',
      category: 'Cafe Updates',
      categoryLabel: 'CAFE UPDATES',
      excerpt:
        'Welcome to Liberica MY Hub — a place where artisan coffee, good food, meaningful conversations and Kuala Lumpur culture come together.',
      date: 'April 28, 2025',
      readTime: '4 min read',
      image: '/assets/images/welcome.jpg',
      author: 'Liberica Team',
      tags: ['Liberica MY Hub', 'Cafe', 'Coffee', 'Kuala Lumpur'],
      sections: [
        {
          heading: 'Welcome to Our Hub',
          paragraphs: [
            'Liberica MY Hub was created as a place where great coffee and meaningful experiences come together.',
            'We believe cafés should be more than places to grab a drink. They should be places where people feel welcome.'
          ],
          image: '/assets/images/welcome-liberica.jpeg'
        },
        {
          heading: 'Coffee, Food and Community',
          paragraphs: [
            'From artisan coffee to carefully selected food and snacks, every part of the experience is designed to complement your visit.',
            'Most importantly, we want to create a space that feels connected to the people and culture around us.'
          ],
          image: '/assets/images/coffee-community.jpeg',
          quote: 'Welcome to a place where every cup has a story.'
        }
      ]
    },

    /* 9. LIBERICA COFFEE */
    {
      id: 9,
      title: 'Why Liberica Coffee Is Special',
      slug: 'why-liberica-coffee-is-special',
      category: 'Coffee Culture',
      categoryLabel: 'COFFEE CULTURE',
      excerpt:
        'Explore the story behind Liberica coffee and discover what makes this unique coffee species so special.',
      date: 'April 24, 2025',
      readTime: '5 min read',
      image: '/assets/images/why-liberica-coffee.jpg',
      author: 'Liberica Team',
      tags: ['Liberica Coffee', 'Coffee Culture', 'Coffee Species'],
      sections: [
        {
          heading: 'A Coffee Species with Character',
          paragraphs: [
            'Liberica is one of the lesser-known coffee species, but its unique characteristics make it fascinating for coffee lovers.',
            'Its beans are known for distinctive aromas, complex flavours and a character that sets them apart from more familiar varieties.'
          ],
          image: '/assets/images/coffee-special.jpg'
        },
        {
          heading: 'A Story Worth Sharing',
          paragraphs: [
            'Coffee is not only about flavour. It is also about history, agriculture, people and culture.',
            'Learning about Liberica coffee helps us appreciate the diversity that exists within the world of coffee.'
          ],
          image: '/assets/images/story-sharing.jpg',
          quote:
            'Different beans create different stories. That is what makes coffee so fascinating.'
        }
      ]
    },

    /* 11. BEHIND THE SCENES */
    {
      id: 11,
      title: 'Behind the Scenes at Liberica MY Hub',
      slug: 'behind-the-scenes-at-liberica-my-hub',
      category: 'Cafe Updates',
      categoryLabel: 'CAFE UPDATES',
      excerpt:
        'Take a look behind the scenes and meet the people who help make every Liberica MY Hub experience memorable.',
      date: 'April 16, 2025',
      readTime: '4 min read',
      image: '/assets/images/behind-liberica.jpg',
      author: 'Liberica Team',
      tags: ['Behind the Scenes', 'Cafe Updates', 'Liberica Team'],
      sections: [
        {
          heading: 'The People Behind Every Cup',
          paragraphs: [
            'A café experience is created by people. From the baristas preparing each beverage to the team members welcoming guests, every person plays an important role.',
            'Their attention to detail helps turn everyday visits into memorable moments.'
          ],
          image: '/assets/images/people-behind.jpeg'
        },
        {
          heading: 'Passion in the Details',
          paragraphs: [
            'Behind every carefully prepared drink is training, practice and a genuine passion for hospitality.',
            'We believe those small details are what guests remember.'
          ],
          image: '/assets/images/machines.jpg',
          quote:
            'The best café experiences are created by people who care about what they do.'
        }
      ]
    },
  ];

  constructor(private route: ActivatedRoute) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      this.currentSlug = params.get('slug') || '';
      this.loadPost();
      window.scrollTo(0, 0);
    });
    this.checkReveal();
  }

  loadPost(): void {
    this.post = this.allPosts.find(item => item.slug === this.currentSlug) || null;
  }

  get relatedPosts(): BlogPostDetail[] {
    const currentPost = this.post;

    if (!currentPost) {
      return [];
    }

    return this.allPosts
      .filter(
        item => item.id !== currentPost.id && item.category === currentPost.category
      )
      .slice(0, 4);
  }

  get otherPosts(): BlogPostDetail[] {
    const currentPost = this.post;

    if (!currentPost) {
      return [];
    }

    return this.allPosts
      .filter(item => item.id !== currentPost.id)
      .slice(0, 4);
  }

  shareFacebook(): void {
    const url = window.location.href;
    window.open(
      'https://www.facebook.com/sharer/sharer.php?u=' + encodeURIComponent(url),
      '_blank'
    );
  }

  shareTwitter(): void {
    if (!this.post) {
      return;
    }

    const url = window.location.href;
    window.open(
      'https://twitter.com/intent/tweet?url=' +
        encodeURIComponent(url) +
        '&text=' +
        encodeURIComponent(this.post.title),
      '_blank'
    );
  }

  shareLinkedIn(): void {
    const url = window.location.href;
    window.open(
      'https://www.linkedin.com/sharing/share-offsite/?url=' + encodeURIComponent(url),
      '_blank'
    );
  }

  copyLink(): void {
    const url = window.location.href;

    if (navigator && navigator.clipboard) {
      navigator.clipboard.writeText(url);
    }
  }

  trackByPost(index: number, post: BlogPostDetail): number {
    return post.id;
  }

      // Scroll Animations
      @HostListener('window:scroll')
      onScroll(): void {
        this.checkReveal();
      }
    
      checkReveal(): void {
        const elements = document.querySelectorAll('.reveal');
        const windowHeight = window.innerHeight;
    
        elements.forEach((el: any) => {
          const position = el.getBoundingClientRect().top;
          if (position < windowHeight - 100) {
            el.classList.add('active');
          }
        });
      }
    
      // Smooth Scroll
      scrollTo(element: HTMLElement): void {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
}